import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import api from '@/api/_legacy'
import { PATH, TENANT } from '@/const'

/**
 * ABSA-10814 (Option A): drives the streamlined "Amend fees" gesture for an ACTIVE
 * model-portfolio account.
 *
 * Fees can only be edited at the Set-economics step, which is reachable once the account has been
 * submitted into the approval cycle. This hook does that behind the scenes:
 *   1. `update/save` re-saves the active account carrying its CURRENT portfolio id. This is
 *      load-bearing: the backend only emits a portfolio instruction (and therefore routes the
 *      amendment to full approval / Set-economics) when the pending state carries the portfolio
 *      identity. The wizard's normal `mapPortfolio` omits it when the model is unchanged, so we send
 *      it explicitly here. Fees themselves are NOT sent now - the per-account fee override is
 *      captured later at the eco-approval step.
 *   2. `submit` moves the account to PendingEconomicsApproval.
 *   3. Navigate to the Trade builder queue with this account pre-selected so the user lands on its
 *      Activation process -> Set economics fee grid.
 */
function messageFrom(e: unknown, fallback: string): string {
  const err = e as {
    error?: { response?: { data?: { title?: string } } }
    message?: string
    response?: { data?: { title?: string } }
  }
  return err?.error?.response?.data?.title ?? err?.response?.data?.title ?? err?.message ?? fallback
}

/**
 * update/save answers 200 with `success: false` + `validationErrors` when it rejects the edit - it
 * does NOT throw (the generated client only throws on a non-2xx response). So the result has to be
 * inspected explicitly, otherwise we would go on to submit an account that was never saved.
 */
function validationMessageFrom(
  result: { success?: boolean; validationErrors?: string[] | null } | undefined,
  fallback: string
): string | undefined {
  if (result?.success !== false) return undefined
  const errors = result.validationErrors?.filter(Boolean)
  return errors?.length ? errors.join(' ') : fallback
}

export function useAmendFees() {
  const navigate = useNavigate()
  const [isAmending, setIsAmending] = useState<boolean>(false)
  const [error, setError] = useState<string | undefined>(undefined)

  const amendFees = async (accountId: string, portfolioId: string) => {
    setIsAmending(true)
    setError(undefined)

    const saveFallback = 'Could not start the fee amendment. Please try again.'
    try {
      const saved = await api.tenant.updateSaveCreate(TENANT, { accountId, portfolioId } as any)

      // A rejected edit comes back as 200 + success:false (no throw), so check before submitting.
      const validationError = validationMessageFrom(saved?.data, saveFallback)
      if (validationError) {
        setError(validationError)
        setIsAmending(false)
        return
      }
    } catch (e) {
      setError(messageFrom(e, saveFallback))
      setIsAmending(false)
      return
    }

    // update/save has put the account into a draft edit. If the submit fails now, the account is left
    // as a resumable draft in the Trade builder, so tell the user how to recover rather than implying
    // nothing happened.
    try {
      await api.tenant.submitCreate(accountId, TENANT)
    } catch (e) {
      setError(
        messageFrom(
          e,
          'The fee amendment was started but could not be submitted. You can complete or discard it from the Trade builder.'
        )
      )
      setIsAmending(false)
      return
    }

    navigate(PATH.trades, { state: { selectedAccountId: accountId } })
  }

  const dismissError = () => setError(undefined)

  return { amendFees, isAmending, error, dismissError }
}
